import { Button, DatePicker, Input, Select, Space, TableProps } from "antd";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import ActionWatchIcon from "../../../../assets/icons/ActionWatchIcon";
import DateIcon from "../../../../assets/icons/DateIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../../assets/icons/ViewIcon";
import LogViewModalKPI from "../../../../components/OwnKPI/LogViewModalKPI";
import CustomTable from "../../../../components/ui/CustomTable";
import { dateFormat, dbDateFormat } from "../../../../constants/constants";
import { useGetDepartmentsQuery } from "../../../../features/department/departmentApi";
import {
  useGetAllKpiQuery,
  useGetOwnKpiQuery,
} from "../../../../features/performanceManagement/performnceManagementApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { formatDate } from "../../../../lib/time";
import {
  GetOwnKpiParams,
  GetOwnKpiResponseType,
} from "../../../../types/performance";
import { countParPageSerialNumber } from "../../../../utils/number";
import {
  addKeyInArray,
  selectArrayOption,
  selectSearchOption,
} from "../../../../utils/utils";
import { findPlanVal } from "../OpenKPISetup/OpenKPISetup";
import {
  planPeriodList,
  planTypeList,
} from "../OpenKPISetup/OpenKPISetupCreate";
import { planTypeColor } from "../OwnKPI/OwnKPI";

export const statusList = {
  pending: {
    text: "Pending for Approval",
    style: "status-warning",
  },
  complete: {
    text: "Approved by Line Manager",
    style: "status-success",
  },
  reject: {
    text: "Reject by Line Manager",
    style: "status-danger",
  },
  default: {
    text: "Not Started",
    style: "status text-[#414141] bg-[#FFF5F4]",
  },
};

export default function KPISetupReport() {
  const { open, handleClose, handleOpen } = useModal<
    { log: any; kpi: string },
    string
  >();
  const {
    page,
    limit,
    filter,
    keyword,
    handlePagination,
    handleLimit,
    handleFilter,
    handleSearch,
  } = useTable<GetOwnKpiParams>();
  const { isLoading, isFetching, isError, error, data } = useGetOwnKpiQuery({
    params: {
      ...filter,
      limit,
      startDate: filter.startDate,
      endDate: filter.startDate,
      title: keyword,
      positionStatus: "kpi",
    },
  });
  const {
    isError: isDepartmentError,
    error: departmentError,
    data: departmentData,
  } = useGetDepartmentsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });
  const {
    isLoading: isKpiLoading,
    isError: isKpiError,
    error: kpiError,
    data: kpiData,
  } = useGetAllKpiQuery({
    params: { limit: 0, skip: 0 },
  });
  useError(isKpiError, kpiError, "KPI get fail");
  useError(isError, error, "Get KPI fail");
  useError(isDepartmentError, departmentError, "Get department data fail");

  const columns: TableProps<
    GetOwnKpiResponseType["data"]["kpis"][0]
  >["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Plan Name",
      width: 200,
      render: (_, record) => record?.kpi?.kpi_title || "n/a",
    },
    {
      title: "Plan Period",
      width: 200,
      dataIndex: "plan_period",
      render: (_, record) =>
        findPlanVal(planPeriodList, record?.kpi?.plan_period),
    },
    {
      title: "Plan Type",
      width: 150,
      dataIndex: "plan_type",
      render: (_, record) => {
        const type = record?.kpi?.plan_type as keyof typeof planTypeColor;
        return (
          <span
            style={planTypeColor[type] ? { color: planTypeColor[type] } : {}}
          >
            {findPlanVal(planTypeList, type)}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 300,
      render: (val) => {
        const st = statusList[val as keyof typeof statusList];
        return <span className={st?.style}>{st?.text}</span>;
      },
    },
    {
      title: "Setup Date",
      dataIndex: "createdAt",
      render: (val) => (val ? formatDate(val) : "n/a"),
    },

    {
      title: "Action",
      align: "center",
      dataIndex: "id",
      render: (val, record) => (
        <Space>
          <Link to={`/admin/kpi-setup/kpi-setup-report/view/${val}`}>
            <Button
              className="action-button"
              size="small"
              icon={<ViewIcon />}
            />
          </Link>
          <Button
            icon={<ActionWatchIcon />}
            size="small"
            className="action-button"
            onClick={() =>
              handleOpen({
                type: "OWN_KPI_LOG_VIEW",
                data: {
                  log: record?.actionLogs,
                  kpi: record?.kpi?.kpi_title,
                },
              })
            }
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <SidebarLayout title="KPI Setup Report List">
        {open.type === "OWN_KPI_LOG_VIEW" && (
          <LogViewModalKPI
            open={open.open}
            data={open?.data}
            handleClose={handleClose}
          />
        )}

        <CustomTable
          loading={isLoading || isFetching}
          limit={limit}
          page={page}
          columns={columns}
          total={data?.data?.totalLength}
          dataSource={addKeyInArray(data?.data?.kpis)}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          filterSection={
            <>
              <Input
                suffix={<SearchIcon />}
                placeholder={`Search...`}
                style={{ minWidth: 170, maxWidth: 180 }}
                onChange={handleSearch}
                className="search"
              />

              <Select
                placeholder="Plan Name"
                size="middle"
                allowClear
                showSearch
                filterOption={selectSearchOption}
                options={kpiData?.data?.kpis?.map((el) => ({
                  label: el?.kpi_title,
                  value: el?.id,
                }))}
                onChange={(val) => {
                  handleFilter("kpiId", val);
                }}
                suffixIcon={<SelectDownIcon />}
                className="min-w-[130px]"
                loading={isKpiLoading}
              />

              <Select
                placeholder="Plan Type"
                size="middle"
                allowClear
                showSearch
                filterOption={selectSearchOption}
                options={selectArrayOption(planTypeOptions, "label", "value")}
                onChange={(val) => {
                  handleFilter("planType", val ? val : undefined);
                }}
                suffixIcon={<SelectDownIcon />}
                className="min-w-[130px]"
              />

              <Select
                allowClear
                showSearch
                placeholder="Department"
                filterOption={selectSearchOption}
                options={selectArrayOption(
                  departmentData?.data?.departments || [],
                  "name",
                  "id"
                )}
                className="min-w-[130px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                onChange={(val) => handleFilter("departmentId", val)}
              />

              {/* <Select
                placeholder="Designation"
                size="middle"
                allowClear
                showSearch
                //   filterOption={selectSearchOption}
                //   options={selectArrayOption(
                //     departmentData?.data?.departments || [],
                //     "name",
                //     "id"
                //   )}
                suffixIcon={<SelectDownIcon />}
                //   loading={isDepartmentLoading}
              /> */}

              <Select
                allowClear
                showSearch
                onChange={(val) => handleFilter("status", val)}
                placeholder="Status"
                filterOption={selectSearchOption}
                options={statusFilterList}
                className="min-w-[130px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                dropdownStyle={{ minWidth: "230px" }}
              />

              <DatePicker
                format={dateFormat}
                style={{ width: 140 }}
                suffixIcon={<DateIcon />}
                // className="clear-button-hide"
                value={filter?.startDate ? dayjs(filter.startDate) : null}
                onChange={(date) => {
                  handleFilter(
                    "startDate",
                    date ? date.format(dbDateFormat) : undefined
                  );
                }}
              />
            </>
          }
        />
      </SidebarLayout>
    </>
  );
}

export const statusFilterList = [
  {
    label: "Pending for Approval",
    value: "pending",
  },
  {
    label: "Approved by Line Manager",
    value: "complete",
  },
  {
    label: "Reject by Line Manager",
    value: "reject",
  },
];

const planTypeOptions = [
  { label: "Performance", value: "performance" },
  { label: "Development", value: "development" },
];
